import differenceInDays from 'date-fns/differenceInCalendarDays';
import React from 'react';
import { ProxyGraphClients, abgGraphqlClient } from '@atlassian/temp-client-utils';
import {
  ApprovalEntity,
  ApprovalEntityInput as ApprovalEntityInfoInput,
  ApprovalType,
  InitiateProcessInput,
  Mutation,
} from '@css-xp/apd-common';
import { evaluateApproval, initiateProcess } from '../queries/initiate-approval';
import { useSubscriptionHelper } from './use-subscription-helper';

interface ApprovalEntityInput extends ApprovalEntityInfoInput {
  billingStartDate?: string;
}

interface Props {
  approvalType: ApprovalType;
  entities?: ApprovalEntityInput[];
  onApprovalComplete?: () => void;
  evaluateOnInit?: boolean;
}

export const useApprovals = ({ approvalType, entities = [], onApprovalComplete, evaluateOnInit = false }: Props) => {
  const [approvalError, setApprovalError] = React.useState<Error | null>(null);
  const [approvalList, setApprovalList] = React.useState<ApprovalEntity[]>([]);
  const [approvalRequired, setApprovalRequired] = React.useState<boolean>(false);
  const [approvalSlackChannel, setApprovalSlackChannel] = React.useState<string | undefined | null>();
  const [loading, setLoading] = React.useState(false);
  const [approvalProcessing, setApprovalProcessing] = React.useState(false);
  const [evaluateApprovalProcessing, setEvaluateApprovalProcessing] = React.useState(false);
  const { getSubscriptionStartDate, error: startDateError } = useSubscriptionHelper();

  const isRefundApproval = React.useMemo(() => {
    return approvalType === ApprovalType.SUBSCRIPTION_REFUND || approvalType === ApprovalType.TAX_REFUND;
  }, [approvalType]);

  const isTrialExtensionApproval = React.useMemo(() => {
    return approvalType === ApprovalType.TRIAL_EXTENSION;
  }, [approvalType]);

  const client = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.APD), []);

  const constructEntitiesForRefund = React.useCallback(
    async (entities: ApprovalEntityInput[]) => {
      const _toReturn: ApprovalEntityInput[] = [];
      for (const entity of entities) {
        if (entity.noOfDays === undefined) {
          const startDate = await getSubscriptionStartDate(entity.entityId, entity?.billingStartDate as string);
          const noOfDays = startDate ? differenceInDays(new Date(), new Date(startDate)) : 0;
          delete entity.billingStartDate;
          _toReturn.push({
            ...entity,
            noOfDays,
          });
        } else {
          delete entity.billingStartDate;
          _toReturn.push({ ...entity });
        }
      }
      return _toReturn;
    },
    [getSubscriptionStartDate]
  );

  const submitApproval = React.useCallback(
    (request: InitiateProcessInput) => {
      (async () => {
        try {
          setApprovalProcessing(true);
          let entities = request.entityInformation;
          if (isRefundApproval) {
            entities = await constructEntitiesForRefund(request.entityInformation as ApprovalEntityInput[]);
          }
          const { data, errors } = await client.mutate<Mutation>({
            mutation: initiateProcess,
            variables: { ...request, entityInformation: entities },
          });
          if (errors && errors.length > 0) {
            throw errors[0];
          }
          const success = !!data?.initiateProcess?.processDefinitionId;
          if (success) {
            onApprovalComplete?.();
            setApprovalError(null);
          }
        } catch (e) {
          setApprovalError(e as Error);
          return false;
        } finally {
          setApprovalProcessing(false);
        }
      })();
    },
    [client, constructEntitiesForRefund, isRefundApproval, onApprovalComplete]
  );

  const evaluate = React.useCallback(
    async (entities: ApprovalEntityInput[]) => {
      let isApprovalRequired = false;
      let slackChannel, error;
      try {
        setApprovalProcessing(true);
        setEvaluateApprovalProcessing(true);
        let entityInformation = entities;
        if (isRefundApproval) {
          entityInformation = await constructEntitiesForRefund(entities);
        }
        if (isTrialExtensionApproval && entityInformation[0] && entityInformation[0].currentTotalTrialLength === null) {
          throw new Error(`Trial length can't be zero`);
        }
        const { data, errors } = await client.mutate<Mutation>({
          mutation: evaluateApproval,
          variables: {
            approvalType,
            entityInformation,
          },
        });
        if (errors && errors.length > 0) {
          throw errors[0];
        }
        if (data) {
          const approvalList = data.evaluateApproval as ApprovalEntity[];
          isApprovalRequired = approvalList.some((d) => d?.approvalRequired);
          setApprovalRequired(isApprovalRequired);
          slackChannel = approvalList.filter((d) => d?.approvalRequired)[0]?.slackChannel;
          setApprovalSlackChannel(slackChannel);
          setApprovalList(approvalList as ApprovalEntity[]);
        }
      } catch (e) {
        error = e;
        setApprovalError(e as Error);
        setApprovalRequired(false);
        setApprovalList([]);
        return null;
      } finally {
        setApprovalProcessing(false);
        setEvaluateApprovalProcessing(false);
      }
      return { isApprovalRequired, slackChannel, error };
    },
    [approvalType, client, constructEntitiesForRefund, isRefundApproval, isTrialExtensionApproval]
  );

  React.useEffect(() => {
    if (!evaluateOnInit) return;
    (async () => {
      setLoading(true);
      await evaluate(entities);
      setLoading(false);
    })();
  }, [approvalType, client, entities, evaluate, evaluateOnInit]);

  React.useEffect(() => {
    if (startDateError) {
      setApprovalError(startDateError);
    }
  }, [startDateError]);

  return {
    approvalRequired,
    approvalList,
    approvalError,
    approvalSlackChannel,
    loading,
    submitApproval,
    approvalProcessing,
    evaluate,
    setApprovalRequired,
    evaluateApprovalProcessing,
  };
};
